import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Link,
  Divider,
  Button,
  Tab,
  Tabs,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  // Tooltip,
  IconButton,
  Collapse
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  GetApp as DownloadIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement } from 'chart.js';
import { Doughnut, Bar, Line } from 'react-chartjs-2';




// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement);

const FOSSAnalysis = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    fetchFOSSAnalysis();
  }, [projectId]);

  const fetchFOSSAnalysis = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/reports/foss-compliance-result?project_id=${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch FOSS analysis data');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'HIGH':
        return '#f44336';
      case 'MEDIUM':
        return '#ff9800';
      case 'LOW':
        return '#4caf50';
      case 'CRITICAL':
        return '#d32f2f';
      default:
        return '#2196f3';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'HIGH':
      case 'CRITICAL':
        return <ErrorIcon style={{ color: getSeverityColor(severity) }} />;
      case 'MEDIUM':
        return <WarningIcon style={{ color: getSeverityColor(severity) }} />;
      case 'LOW':
        return <InfoIcon style={{ color: getSeverityColor(severity) }} />;
      default:
        return <InfoIcon style={{ color: getSeverityColor(severity) }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'fixed':
        return 'success';
      case 'will_not_fix':
        return 'error';
      case 'affected':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getAllVulnerabilities = () => {
    if (!data || !data.results) return [];

    return data.results.flatMap(result =>
      result.Vulnerabilities?.map(vuln => ({
        ...vuln,
        Target: result.Target,
        Class: result.Class,
        Type: result.Type
      })) || []
    );
  };

  const getSeverityStats = () => {
    const vulnerabilities = getAllVulnerabilities();
    const stats = {};

    vulnerabilities.forEach(vuln => {
      const severity = vuln.Severity || 'Unknown';
      stats[severity] = (stats[severity] || 0) + 1;
    });

    return stats;
  };

  const getPackageStats = () => {
    const vulnerabilities = getAllVulnerabilities();
    const packageStats = {};

    vulnerabilities.forEach(vuln => {
      const pkg = vuln.PkgName || 'Unknown';
      if (!packageStats[pkg]) {
        packageStats[pkg] = { total: 0, high: 0, medium: 0, low: 0, critical: 0 };
      }
      packageStats[pkg].total++;
      const severity = vuln.Severity?.toLowerCase() || 'unknown';
      if (packageStats[pkg][severity] !== undefined) {
        packageStats[pkg][severity]++;
      }
    });

    return packageStats;
  };

  const getFilteredVulnerabilities = () => {
    const vulnerabilities = getAllVulnerabilities();

    return vulnerabilities.filter(vuln => {
      const matchesSearch = !searchTerm ||
        vuln.VulnerabilityID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vuln.PkgName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vuln.Title?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesSeverity = severityFilter === 'all' ||
        vuln.Severity?.toLowerCase() === severityFilter.toLowerCase();

      return matchesSearch && matchesSeverity;
    });
  };

  const renderSeverityChart = () => {
    const stats = getSeverityStats();

    const chartData = {
      labels: Object.keys(stats),
      datasets: [
        {
          data: Object.values(stats),
          backgroundColor: Object.keys(stats).map(severity => getSeverityColor(severity)),
          borderWidth: 2,
          borderColor: '#fff'
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
        },
        title: {
          display: true,
          text: 'Vulnerability Distribution by Severity'
        }
      }
    };

    return (
      <Paper elevation={3} sx={{ p: 2, height: 400 }}>
        <Doughnut data={chartData} options={options} />
      </Paper>
    );
  };

  const renderPackageChart = () => {
    const packageStats = getPackageStats();
    if (Object.keys(packageStats).length === 0) {
      return <Alert severity="info">No package vulnerability data available.</Alert>;
    }
    const topPackages = Object.entries(packageStats)
      .sort(([, a], [, b]) => b.total - a.total)
      .slice(0, 10);

    const chartData = {
      labels: topPackages.map(([pkg]) => pkg),
      datasets: [
        {
          label: 'Critical',
          data: topPackages.map(([, stats]) => stats.critical),
          backgroundColor: getSeverityColor('critical'),
        },
        {
          label: 'High',
          data: topPackages.map(([, stats]) => stats.high),
          backgroundColor: getSeverityColor('high'),
        },
        {
          label: 'Medium',
          data: topPackages.map(([, stats]) => stats.medium),
          backgroundColor: getSeverityColor('medium'),
        },
        {
          label: 'Low',
          data: topPackages.map(([, stats]) => stats.low),
          backgroundColor: getSeverityColor('low'),
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Top 10 Packages by Vulnerability Count'
        }
      },
      scales: {
        x: {
          stacked: true,
        },
        y: {
          stacked: true,
        }
      }
    };

    return (
      <Paper elevation={3} sx={{ p: 2, height: 400 }}>
        <Bar data={chartData} options={options} />
      </Paper>
    );
  };

  const renderOverviewCards = () => {
    const vulnerabilities = getAllVulnerabilities();
    const stats = getSeverityStats();
    if (Object.keys(stats).length === 0) {
      return <Alert severity="info">No severity data available to display.</Alert>;
    }

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="overline">
                    Total Vulnerabilities
                  </Typography>
                  <Typography variant="h4" component="div">
                    {vulnerabilities.length}
                  </Typography>
                </Box>
                <ErrorIcon sx={{ fontSize: 40, color: 'error.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="overline">
                    High/Critical
                  </Typography>
                  <Typography variant="h4" component="div">
                    {(stats.HIGH || 0) + (stats.CRITICAL || 0)}
                  </Typography>
                </Box>
                <WarningIcon sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="overline">
                    Analysis Status
                  </Typography>
                  <Typography variant="h6" component="div">
                    {data?.analysisStatus || 'Unknown'}
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="overline">
                    Analysis Time
                  </Typography>
                  <Typography variant="h6" component="div">
                    {data?.analysisTime ? `${parseFloat(data.analysisTime).toFixed(2)}s` : 'N/A'}
                  </Typography>
                </Box>
                <InfoIcon sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderVulnerabilityTable = () => {
    const filteredVulnerabilities = getFilteredVulnerabilities();
    const paginatedVulnerabilities = filteredVulnerabilities.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    const handleExpandRow = (vulnId) => {
      const newExpanded = new Set(expandedRows);
      if (newExpanded.has(vulnId)) {
        newExpanded.delete(vulnId);
      } else {
        newExpanded.add(vulnId);
      }
      setExpandedRows(newExpanded);
    };

    return (
      <Paper elevation={3}>
        <Box p={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search vulnerabilities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon />
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                >
                  <MenuItem value="all">All Severities</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="textSecondary">
                Showing {filteredVulnerabilities.length} vulnerabilities
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Details</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Package</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Current Version</TableCell>
                <TableCell>Fixed Version</TableCell>
                <TableCell>Target</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {
                paginatedVulnerabilities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Alert severity="info">No matching vulnerabilities found.</Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedVulnerabilities.map((vuln, index) => (
                    <React.Fragment key={vuln.VulnerabilityID + index}>
                      <TableRow>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleExpandRow(vuln.VulnerabilityID + index)}
                          >
                            {expandedRows.has(vuln.VulnerabilityID + index) ?
                              <ExpandMoreIcon /> : <ExpandMoreIcon style={{ transform: 'rotate(-90deg)' }} />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Link href={vuln.PrimaryURL} target="_blank" rel="noopener">
                            {vuln.VulnerabilityID}
                          </Link>
                        </TableCell>
                        <TableCell>{vuln.PkgName}</TableCell>
                        <TableCell>
                          <Chip
                            icon={getSeverityIcon(vuln.Severity)}
                            label={vuln.Severity}
                            style={{ backgroundColor: getSeverityColor(vuln.Severity), color: 'white' }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={vuln.Status}
                            color={getStatusColor(vuln.Status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{vuln.InstalledVersion}</TableCell>
                        <TableCell>{vuln.FixedVersion}</TableCell>
                        <TableCell>{vuln.Target}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} style={{ padding: 0 }}>
                          <Collapse in={expandedRows.has(vuln.VulnerabilityID + index)} timeout="auto" unmountOnExit>
                            <Box p={2} bgcolor="grey.50">
                              <Typography variant="h6" gutterBottom>
                                {vuln.Title}
                              </Typography>
                              <Typography variant="body2" paragraph>
                                {vuln.Description}
                              </Typography>

                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2">CVSS Scores:</Typography>
                                  {vuln.CVSS && Object.entries(vuln.CVSS).map(([source, cvss]) => (
                                    <Typography key={source} variant="body2">
                                      {source}: {cvss.V3Score} ({cvss.V3Vector})
                                    </Typography>
                                  ))}
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2">CWE IDs:</Typography>
                                  {vuln.CweIDs?.map(cwe => (
                                    <Chip key={cwe} label={cwe} size="small" sx={{ mr: 1, mb: 1 }} />
                                  ))}
                                </Grid>
                              </Grid>

                              <Typography variant="subtitle2" sx={{ mt: 2 }}>
                                References (showing first 5):
                              </Typography>
                              <List dense>
                                {vuln.References?.slice(0, 5).map((ref, idx) => (
                                  <ListItem key={idx} disablePadding>
                                    <Link href={ref} target="_blank" rel="noopener" variant="body2">
                                      {ref}
                                    </Link>
                                  </ListItem>
                                ))}
                                {vuln.References?.length > 5 && (
                                  <ListItem>
                                    <Typography variant="body2" color="textSecondary">
                                      ... and {vuln.References.length - 5} more references
                                    </Typography>
                                  </ListItem>
                                )}
                              </List>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  )))
              }
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredVulnerabilities.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
    );
  };

  const renderAnalysisInfo = () => {
    return (
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Analysis Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="textSecondary">Run ID:</Typography>
            <Typography variant="body1">{data?.run_id}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="textSecondary">Created At:</Typography>
            <Typography variant="body1">
              {data?.createdAt ? new Date(data.createdAt * 1000).toLocaleString() : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="textSecondary">Analysis Status:</Typography>
            <Typography variant="body1">{data?.analysisStatus}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="textSecondary">Analysis Time:</Typography>
            <Typography variant="body1">
              {data?.analysisTime ? `${parseFloat(data.analysisTime).toFixed(2)} seconds` : 'N/A'}
            </Typography>
          </Grid>
        </Grid>

        {(data?.s3_json_file || data?.s3_csv_file) && (
          <Box mt={3}>
            <Typography variant="subtitle2" gutterBottom>Download Reports:</Typography>
            <Box display="flex" gap={1}>
              {data?.s3_json_file && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => window.open(data.s3_json_file, '_blank')}
                >
                  JSON Report
                </Button>
              )}
              {data?.s3_csv_file && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => window.open(data.s3_csv_file, '_blank')}
                >
                  CSV Report
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading FOSS analysis: {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No FOSS analysis data available.
      </Alert>
    );
  }

  const handleBackToProjectList = () => {
    navigate('/projects'); // Adjust the route as per your routing structure
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <Typography variant="h4" gutterBottom>
        FOSS Compliance Analysis
      </Typography>

      <Button
                variant="contained"
                className="athrv-dash-primary-button"
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#263d68" : undefined,
                }}
                onClick={() => navigate("/projects")}
              >
                Back to Project List
              </Button>
    </Box>

      {renderAnalysisInfo()}
      {getAllVulnerabilities().length > 0 && renderOverviewCards()}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          {renderSeverityChart()}
        </Grid>
        <Grid item xs={12} lg={6}>
          {renderPackageChart()}
        </Grid>
      </Grid>

      <Paper elevation={3}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Vulnerabilities" />
          <Tab label="Targets" />
        </Tabs>

        <Box hidden={activeTab !== 0}>
          {renderVulnerabilityTable()}
        </Box>

        <Box hidden={activeTab !== 1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Target</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Vulnerabilities</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.results?.map((result, index) => (
                  <TableRow key={index}>
                    <TableCell>{result.Target}</TableCell>
                    <TableCell>{result.Class}</TableCell>
                    <TableCell>{result.Type}</TableCell>
                    <TableCell>{result.Vulnerabilities?.length || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>
    </Box>
  );
};

export default FOSSAnalysis;